import * as React from 'react'
import { CaretLeft, Check, X, Star } from '@phosphor-icons/react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { SkillFormData } from './types'
import { SkillActivityFeed } from 'components/updates-drawer/skill-activity-feed'
import styles from '../../styles/scrollbar-hide.module.css'

type SkillFormViewProps = {
  name: string
  initialDescription: string
  initialSkillType: 'hard' | 'soft' | null
  initialProficiencyLevel: number | null
  initialStarred: boolean
  // For tracking modifications (used in add mode)
  canonicalDescription?: string
  canonicalSkillType?: 'hard' | 'soft' | null
  showBackButton?: boolean
  onBack?: () => void
  onClose: () => void
  onSave: (data: SkillFormData) => void
  saving: boolean
  skillId?: string
  userId?: string
}

export const SkillFormView: React.FC<SkillFormViewProps> = ({
  name,
  initialDescription,
  initialSkillType,
  initialProficiencyLevel,
  initialStarred,
  canonicalDescription,
  canonicalSkillType,
  showBackButton = false,
  onBack,
  onClose,
  onSave,
  saving,
  skillId,
  userId,
}) => {
  const [description, setDescription] = React.useState(initialDescription)
  const [skillType, setSkillType] = React.useState<'hard' | 'soft'>(
    initialSkillType || 'hard'
  )
  const [proficiencyLevel, setProficiencyLevel] = React.useState<number>(
    initialProficiencyLevel || 1
  )
  const [starred, setStarred] = React.useState(initialStarred)

  const handleSave = () => {
    // If canonical values are provided, only send modified values
    const shouldTrackModifications =
      canonicalDescription !== undefined || canonicalSkillType !== undefined

    let finalDescription: string | null = description || null
    let finalSkillType: 'hard' | 'soft' | null = skillType

    if (shouldTrackModifications) {
      // Only send if user modified from canonical defaults
      finalDescription =
        description !== (canonicalDescription || '')
          ? description || null
          : null
      finalSkillType =
        skillType !== (canonicalSkillType || 'hard') ? skillType : null
    }

    onSave({
      name,
      description: finalDescription,
      skill_type: finalSkillType,
      proficiency_level: proficiencyLevel,
      starred,
    })
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 p-4 border-b border-gray-200">
        <div className="flex items-center gap-2">
          {showBackButton && onBack && (
            <button
              onClick={onBack}
              className="p-1 rounded hover:bg-gray-100 text-gray-500"
            >
              <CaretLeft className="h-5 w-5" weight="bold" />
            </button>
          )}
          <h2 className="text-lg font-semibold text-gray-900">{name}</h2>
          <button
            onClick={() => setStarred(!starred)}
            className="p-1 rounded hover:bg-gray-100"
          >
            <Star
              className={`h-5 w-5 ${
                starred ? 'text-theme-50 fill-theme-50' : 'text-gray-400'
              }`}
              weight={starred ? 'fill' : 'regular'}
            />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="w-8 h-8 flex items-center justify-center hover:bg-[#e7eaf4] rounded-full transition-colors"
          >
            <Check className="h-4 w-4 text-gray-600" />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center bg-[#e7eaf4] rounded-full transition-colors"
            title="Close"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className={`flex-1 overflow-y-auto p-4 ${styles.hideScrollbar}`}>
        <div className="mb-6">
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Add a description..."
            className="w-full max-h-[150px] p-3 border border-gray-200 rounded-lg text-sm text-gray-700 resize-none focus:outline-none focus:border-transparent"
            rows={10}
            maxLength={1024}
          />
        </div>

        <div className="flex items-center gap-4 mb-6">
          <span className="text-sm text-gray-700 w-32">Skill proficiency</span>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((level) => (
              <button
                key={level}
                onClick={() => setProficiencyLevel(level)}
                className={`w-3 h-3 rounded-full border-2 transition-colors ${
                  proficiencyLevel >= level
                    ? 'bg-theme-50 border-theme-50'
                    : 'bg-gray-200 border-gray-300 hover:border-gray-400'
                }`}
              />
            ))}
          </div>
        </div>

        <div className="flex items-center gap-4 mb-6">
          <span className="text-sm text-gray-700 w-32">Skill type</span>
          <DropdownMenu.Root>
            <DropdownMenu.Trigger asChild>
              <button className="flex items-center gap-2 text-sm text-gray-700 hover:text-gray-900">
                {skillType.charAt(0).toUpperCase() + skillType.slice(1)} skill
                <CaretLeft className="h-4 w-4 rotate-[-90deg]" />
              </button>
            </DropdownMenu.Trigger>
            <DropdownMenu.Portal>
              <DropdownMenu.Content
                className="bg-white rounded-md shadow-lg border border-gray-200 p-1 min-w-[140px]"
                align="start"
              >
                <DropdownMenu.Item
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer outline-none"
                  onSelect={() => setSkillType('hard')}
                >
                  Hard skill
                </DropdownMenu.Item>
                <DropdownMenu.Item
                  className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded cursor-pointer outline-none"
                  onSelect={() => setSkillType('soft')}
                >
                  Soft skill
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Portal>
          </DropdownMenu.Root>
        </div>

        {skillId && userId && (
          <div className="border-t border-gray-200 pt-6">
            <h3 className="font-medium text-sm mb-4">Activity</h3>
            <SkillActivityFeed skillId={skillId} userId={userId} />
          </div>
        )}
      </div>
    </div>
  )
}
